import React, { useState, useEffect, useCallback } from 'react';
import { managerApi, userApi } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Toaster, toast } from 'sonner';
import { 
  ArrowLeft, 
  Check, 
  X, 
  UserPlus,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  Search,
  AlertTriangle
} from 'lucide-react';

const ManagerRequests = ({ onBack }) => {
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  
  // User search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Expiry message
  const [expiredMessage, setExpiredMessage] = useState(null);

  useEffect(() => {
    loadRequests();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length >= 3) {
      const timer = setTimeout(() => {
        searchUsers(searchQuery);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery]);

  const searchUsers = async (query) => {
    setSearching(true);
    try {
      console.log('Searching for:', query);
      const result = await userApi.search(query);
      console.log('Search results:', result);
      setSearchResults(result.users || []);
      setShowResults(true);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
      setShowResults(true); // Still show "no results" message
    } finally {
      setSearching(false);
    }
  };

  const selectUser = (user) => {
    setInviteEmail(user.email);
    setSearchQuery(user.display_name);
    setShowResults(false);
    setSearchResults([]);
  };

  const loadRequests = async () => {
    setLoading(true);
    try {
      const [incoming, sentWithStatus] = await Promise.all([
        managerApi.getRequests(),
        managerApi.getMyRequestsWithStatus(),
      ]);
      setIncomingRequests(incoming);
      setMyRequests(sentWithStatus.requests || []);
      
      // Show expired message if any
      if (sentWithStatus.expired_message) {
        setExpiredMessage(sentWithStatus.expired_message);
        toast.warning(sentWithStatus.expired_message, { duration: 8000 });
      }
    } catch (err) {
      console.error('Failed to load requests:', err);
      // Fallback to old API if new one not available
      try {
        const [incoming, sent] = await Promise.all([
          managerApi.getRequests(),
          managerApi.getMyRequests(),
        ]);
        setIncomingRequests(incoming);
        setMyRequests(sent);
      } catch (e) {
        toast.error('Failed to load requests');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (requestId, action) => {
    setProcessingId(requestId);
    try {
      await managerApi.handleRequest(requestId, action);
      toast.success(`Request ${action === 'approve' ? 'approved' : 'rejected'}`);
      loadRequests();
    } catch (err) {
      console.error('Action failed:', err);
      toast.error(`Failed to ${action} request`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleInviteManager = async () => {
    if (!inviteEmail.trim()) return;
    
    setInviting(true);
    try {
      await managerApi.invite(inviteEmail);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setSearchQuery('');
      setShowInviteDialog(false);
      loadRequests();
    } catch (err) {
      console.error('Invite failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const getStatusBadge = (status, isExpired) => {
    if (isExpired) {
      return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Expired</Badge>;
    }
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8" data-testid="requests-loading">
        <div className="max-w-3xl mx-auto">
          <Skeleton className="h-10 w-48 mb-8" />
          <Skeleton className="h-32 mb-4" />
          <Skeleton className="h-32 mb-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="manager-requests-page">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-button">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-lg font-semibold">Manager Requests</h1>
          </div>
          
          <Dialog open={showInviteDialog} onOpenChange={(open) => {
            setShowInviteDialog(open);
            if (!open) {
              setSearchQuery('');
              setInviteEmail('');
              setSearchResults([]);
              setShowResults(false);
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="invite-manager-button">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Manager
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Invite Manager</DialogTitle>
                <DialogDescription>
                  Search for your manager by name or email address. Type at least 3 characters to search.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2 relative">
                  <Label htmlFor="manager-search">Search by Name or Email</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="manager-search"
                      placeholder="Type at least 3 characters..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setInviteEmail(''); // Clear selected email when typing
                      }}
                      className="pl-9"
                      data-testid="manager-search-input"
                    />
                    {searching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                      </div>
                    )}
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showResults && searchResults.length > 0 && (
                    <div className="w-full mt-2 bg-white border-2 border-primary rounded-md shadow-lg max-h-48 overflow-auto">
                      {searchResults.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => selectUser(user)}
                          className="w-full px-4 py-3 text-left hover:bg-slate-100 border-b last:border-b-0 transition-colors"
                          data-testid={`search-result-${user.id}`}
                        >
                          <div className="font-medium text-sm">{user.display_name}</div>
                          <div className="text-xs text-muted-foreground">{user.email}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {showResults && searchResults.length === 0 && searchQuery.length >= 3 && !searching && (
                    <div className="text-sm text-muted-foreground mt-2 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                      <AlertTriangle className="w-4 h-4 inline mr-2 text-yellow-600" />
                      No users found matching "{searchQuery}". You can still enter an email manually below.
                    </div>
                  )}
                  
                  {searching && (
                    <div className="text-sm text-muted-foreground mt-2 p-3 bg-blue-50 rounded-md">
                      Searching...
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="manager-email">
                    Manager Email {inviteEmail && <span className="text-emerald-600 text-xs">(selected)</span>}
                  </Label>
                  <Input
                    id="manager-email"
                    type="email"
                    placeholder="manager@therapymgmt.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    data-testid="manager-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Double-check the email address is correct. Invitations expire after 2 days if not accepted.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleInviteManager}
                  disabled={!inviteEmail.trim() || inviting}
                  data-testid="send-invite-button"
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* Expired Message Banner */}
        {expiredMessage && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">{expiredMessage}</p>
              <p className="text-xs text-yellow-600 mt-1">
                Please verify the manager's email address is spelled correctly before resending.
              </p>
            </div>
          </div>
        )}

        {/* Incoming Requests */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Pending Requests
          </h2>
          
          {incomingRequests.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No pending requests
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {incomingRequests.map((request) => (
                <Card key={request.id} data-testid={`incoming-request-${request.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{request.requester_name}</p>
                        <p className="text-sm text-muted-foreground">{request.requester_email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Requested on {formatDate(request.created_at)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleAction(request.id, 'reject')}
                          disabled={processingId === request.id}
                          data-testid={`reject-${request.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleAction(request.id, 'approve')}
                          disabled={processingId === request.id}
                          data-testid={`approve-${request.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* My Sent Invitations */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-orange-500" />
            My Invitations
          </h2>
          
          {myRequests.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <p>You haven't invited any managers yet</p>
                <p className="text-xs mt-2">Click "Invite Manager" to request access for your manager</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myRequests.map((request) => (
                <Card key={request.id} data-testid={`my-request-${request.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">{request.manager_email}</CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                          Invited on {formatDate(request.created_at)}
                          {request.days_pending > 0 && request.status === 'pending' && (
                            <span className="ml-2 text-yellow-600">
                              ({request.days_pending} day{request.days_pending !== 1 ? 's' : ''} pending)
                            </span>
                          )}
                        </p>
                      </div>
                      {getStatusBadge(request.status, request.is_expired)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ManagerRequests;
